import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as api from "../../api";
import {useTranslation} from "react-i18next";
import {useFeedbackTools} from "../../components/FeedbackTools";
import {useNavigate} from "react-router-dom";


export const useStationsQuery = (filters) => {
    return useQuery({
        queryKey: ['stations', filters],
        queryFn: () => api.Station.getStations(filters),
        keepPreviousData: true
    });
}

export const useStationQuery = (stationId) => {
    return useQuery({
        queryKey: ['stations', stationId],
        queryFn: () => api.Station.getStation(stationId)
    });
}

export const useCreateStation = () => {
    const {t} = useTranslation(["stations"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async (data) => await api.Station.createStation(data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['stations']);
            showSuccessNotification(t('query.createSuccess'));
            navigate(`/stations/${data.id}`);
        }
    });
}

export const useUpdateStation = (stationId) => {
    const {t} = useTranslation(["stations"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async (data) => await api.Station.updateStation(stationId, data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['stations']);
            showSuccessNotification(t('query.updateSuccess'));
            navigate(`/stations/${data.id}`);
        }
    });
}

export const useDeleteStation = (stationId) => {
    const {t} = useTranslation(["stations"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async () => await api.Station.deleteStation(stationId),
        onSuccess: async () => {
            await navigate(`/stations`);
            queryClient.invalidateQueries(['stations']);
            showSuccessNotification(t('query.deleteSuccess'));
        }
    });
}

export const useCreateStationPhoto = (stationId) => {
    const {t} = useTranslation(["stations"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (file) => await api.StationPhoto.createStationPhoto(stationId, file),
        onSuccess: () => {
            queryClient.invalidateQueries(['stations', stationId]);
            showSuccessNotification(t('query.createPhotoSuccess'));
        }
    })
}

export const useDeleteStationPhoto = (stationId) => {
    const {t} = useTranslation(["stations"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (photoId) => await api.StationPhoto.deleteStationPhoto(stationId, photoId),
        onSuccess: () => {
            queryClient.invalidateQueries(['stations', stationId]);
            showSuccessNotification(t('query.deletePhotoSuccess'));
        }
    })
}

export const useCreateStationLink = (stationId) => {
    const {t} = useTranslation(["stations"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.StationLink.createStationLink(stationId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['stations', stationId]);
            showSuccessNotification(t('query.createLinkSuccess'));
        }
    })
}

export const useDeleteStationLink = (stationId) => {
    const {t} = useTranslation(["stations"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (linkId) => await api.StationLink.deleteStationLink(stationId, linkId),
        onSuccess: () => {
            queryClient.invalidateQueries(['stations', stationId]);
            showSuccessNotification(t('query.deleteLinkSuccess'));
        }
    })
}

export const useStationsExportMutation = () => {
    return useMutation({
        mutationFn: async (data) => await api.Station.exportStations(data),
        onSuccess: (data) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([data.data]));
            link.download = data.fileName; // specify the filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    });
}


